import { ReactElement, ReactNode, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import DraggableToolWindow from '~/components/toolbar/shared/DraggableToolWindow';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { selectedToolState, Tools } from '~/state/atoms/toolsAtoms';

/**
 * The engine tools that place an annotation on the image of the tile they
 * are pressed on: the only tools this entry offers.
 */
type AnnotationTool = Extract<Tools, 'measurement' | 'intensity'>;

interface AnnotationToolbarEntryProps {
    /** Engine tool this entry selects; clicking again returns to pan. */
    tool: AnnotationTool;
    icon: ReactElement;
    tooltipId: string;
    titleId: string;
    testId: string;
    /** Fixed window width, px. */
    width: number;
    /** The panel shown while the tool is selected. */
    children: ReactNode;
}

/**
 * Toolbar entry for an annotation tool: a button that selects the engine tool,
 * and the draggable window that stays open while it is selected. Shared by the
 * measurement and intensity entries, which differ only in these props.
 *
 * An annotation is keyed to the image of the tile it is drawn on and its
 * overlay hangs on every tile, so the tool arms wherever a tile is on screen
 * and the window floats over the grid.
 */
export default function AnnotationToolbarEntry({
    tool,
    icon,
    tooltipId,
    titleId,
    testId,
    width,
    children,
}: AnnotationToolbarEntryProps) {
    const [selectedTool, setTool] = useRecoilState(selectedToolState);
    const anchorRef = useRef<HTMLElement | null>(null);
    const intl = useIntl();
    const isSelected = selectedTool === tool;

    return (
        <>
            <ToolbarButton
                anchorRef={anchorRef}
                icon={icon}
                tooltip={intl.formatMessage({ id: tooltipId })}
                selected={isSelected}
                onClick={() => setTool(isSelected ? 'pan' : tool)}
                data-testid={testId}
            />
            <DraggableToolWindow
                open={isSelected}
                title={intl.formatMessage({ id: titleId })}
                width={width}
                anchorRef={anchorRef}
                onClose={() => setTool('pan')}
            >
                {children}
            </DraggableToolWindow>
        </>
    );
}
